import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  AlertCircle,
  Info,
  ChevronDown,
  ChevronUp,
  X,
  Truck,
  FileText,
  CreditCard,
  Wrench,
} from 'lucide-react';
import { DashboardAlert } from '../../types/dashboard';

interface Props {
  alerts: DashboardAlert[];
  onDismiss?: (id: string) => void;
}

const getAlertIcon = (type: DashboardAlert['type']) => {
  switch (type) {
    case 'overdue_debt':
      return <CreditCard className="w-5 h-5" />;
    case 'idle_vehicle':
      return <Truck className="w-5 h-5" />;
    case 'incomplete_trip':
      return <FileText className="w-5 h-5" />;
    case 'high_expense':
      return <AlertTriangle className="w-5 h-5" />;
    case 'document_expiry':
      return <FileText className="w-5 h-5" />;
    case 'maintenance_due':
      return <Wrench className="w-5 h-5" />;
    default:
      return <Info className="w-5 h-5" />;
  }
};

const getSeverityStyles = (severity: DashboardAlert['severity']) => {
  switch (severity) {
    case 'error':
      return {
        bg: 'bg-red-50',
        border: 'border-red-200',
        icon: 'text-red-600',
        text: 'text-red-800',
      };
    case 'warning':
      return {
        bg: 'bg-yellow-50',
        border: 'border-yellow-200',
        icon: 'text-yellow-600',
        text: 'text-yellow-800',
      };
    case 'info':
    default:
      return {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        icon: 'text-blue-600',
        text: 'text-blue-800',
      };
  }
};

export const AlertPanel: React.FC<Props> = ({ alerts, onDismiss }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(
    alerts.some((a) => a.severity === 'error')
  );

  if (alerts.length === 0) {
    return null;
  }

  const errorCount = alerts.filter((a) => a.severity === 'error').length;
  const warningCount = alerts.filter((a) => a.severity === 'warning').length;

  const handleAlertClick = (alert: DashboardAlert) => {
    if (alert.entityType === 'trip' && alert.entityId) {
      navigate(`/trips/${alert.entityId}`);
    } else if (alert.entityType === 'vehicle') {
      navigate('/vehicles');
    } else if (alert.entityType === 'supplier') {
      navigate('/debt');
    } else if (alert.entityType === 'customer') {
      navigate('/debt');
    }
  };

  return (
    <div className="card">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center space-x-3">
          <AlertTriangle className="w-5 h-5 text-yellow-600" />
          <span className="font-semibold">{t('dashboard.alerts')}</span>
          <div className="flex items-center space-x-2">
            {errorCount > 0 && (
              <span className="px-2 py-0.5 text-xs bg-red-100 text-red-800 rounded-full">
                {errorCount} {t('dashboard.critical')}
              </span>
            )}
            {warningCount > 0 && (
              <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                {warningCount} {t('dashboard.warning')}
              </span>
            )}
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>

      {/* Alert List */}
      {isExpanded && (
        <div className="border-t divide-y">
          {alerts.map((alert) => {
            const styles = getSeverityStyles(alert.severity);
            return (
              <div
                key={alert.id}
                className={`flex items-start p-4 ${styles.bg} cursor-pointer hover:opacity-90 transition-opacity`}
                onClick={() => handleAlertClick(alert)}
              >
                <div className={`mr-3 mt-0.5 ${styles.icon}`}>
                  {getAlertIcon(alert.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`font-medium ${styles.text}`}>{alert.title}</p>
                  <p className="text-sm text-gray-600 mt-0.5">{alert.message}</p>
                </div>
                {onDismiss && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDismiss(alert.id);
                    }}
                    className="ml-2 p-1 hover:bg-white rounded transition-colors"
                  >
                    <X className="w-4 h-4 text-gray-400" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
